import type { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { useCallback, useContext, useMemo, useState } from 'react';
import { buildUpdatedPreferencesAfterSave } from '../../production/helpers/savedFiltersDataUtils';
import { useSavePreferences } from '../auth/hooks/savePreferences';
import { UserContext } from '../auth/UserContext';
import { FORMAT_API_ACTION } from '../constants/reportConstants';
import type { SavePreferenceFormData } from '../types/savePreference';
import type { GridSettingsPreference } from './DataGrid/gridSettings/types';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import type { SavedColumnView } from './columnViewTypes';
import { mapPreferenceToSavedColumnView, serializeColumnViewPreferenceValue } from './columnViewUtils';
import { getColumnViewPreferenceKey } from './columnVisibilitySelectorConstants';

/** Matches buildUpdatedPreferencesAfterSave for newly created preferences. */
const OWN_VIEW_SECURITY = { allowedActions: ['Edit', 'Delete'] };

type SaveColumnViewInput = SavePreferenceFormData & {
    columnVisibilityModel: GridColumnVisibilityModel;
    orderedFields: string[];
    gridSettings?: GridSettingsPreference;
    preferenceId?: string;
    accountId?: string;
};

export function useColumnViews(gridId: string, enabled: boolean) {
    const {
        preferences,
        preferencesLoaded,
        getSharedPreferences,
        refreshPreferences,
        deletePreference,
        userProfile,
        application,
        activeOrganizationAccount,
        organizationAccountDetails
    } = useContext(UserContext);
    const { savePreferences } = useSavePreferences();
    const [savingView, setSavingView] = useState(false);
    const [deletingView, setDeletingView] = useState(false);

    const preferenceKey = useMemo(() => getColumnViewPreferenceKey(gridId), [gridId]);

    const savedViews: SavedColumnView[] = useMemo(() => {
        if (!enabled || !preferencesLoaded) {
            return [];
        }
        const entries = getSharedPreferences(null, null, preferenceKey) || [];
        return entries
            .map((preference) => mapPreferenceToSavedColumnView(preference))
            .filter((view): view is SavedColumnView => Boolean(view));
    }, [enabled, preferencesLoaded, getSharedPreferences, preferenceKey]);

    const saveView = useCallback(
        async ({
            name,
            description,
            shared,
            columnVisibilityModel,
            orderedFields,
            gridSettings,
            preferenceId,
            accountId
        }: SaveColumnViewInput): Promise<SavedColumnView | null> => {
            if (!enabled || !userProfile?.accountId) {
                return null;
            }

            setSavingView(true);
            const trimmedName = name.trim();
            const trimmedDescription = description?.trim() || '';
            const value = serializeColumnViewPreferenceValue(columnVisibilityModel, orderedFields, gridSettings);
            const summary = { about: trimmedDescription, headline: '' };
            // Updates require preference account (same as saved filters).
            const preferenceAccountId =
                accountId || (preferenceId ? activeOrganizationAccount || userProfile.accountId : undefined);

            try {
                const res = await savePreferences({
                    preferenceInput: {
                        ...(preferenceId && { identity: { id: preferenceId } }),
                        ...(preferenceAccountId && { accountId: preferenceAccountId }),
                        view: '',
                        key: preferenceKey,
                        value,
                        agent: '',
                        name: trimmedName,
                        summary
                    },
                    shareInput: shared
                        ? {
                              sharedWithOrganizationAccounts: [
                                  {
                                      actor: {
                                          id: activeOrganizationAccount,
                                          name: organizationAccountDetails?.organizationName || '',
                                          email: organizationAccountDetails?.adminEmail || ''
                                      },
                                      action: FORMAT_API_ACTION.manage
                                  }
                              ],
                              notify: false
                          }
                        : null,
                    showSuccessMessages: false
                });

                if (!res?.id) {
                    return null;
                }

                const updatedPreferences = buildUpdatedPreferencesAfterSave({
                    preferences,
                    preferenceId,
                    savedPreferenceId: res.id,
                    value,
                    name: trimmedName,
                    summary,
                    shared,
                    userAccountId: userProfile.accountId,
                    applicationName: application.name,
                    preferenceKey
                });

                await refreshPreferences(undefined, updatedPreferences, false);

                const persisted = updatedPreferences.find((pref) => pref.id === res.id);
                const mapped = persisted ? mapPreferenceToSavedColumnView(persisted) : null;
                if (mapped) {
                    return mapped;
                }

                // Optimistic fallback when mapping fails: own creates get Edit/Delete; updates keep prior security.
                return {
                    id: res.id,
                    name: trimmedName,
                    description: trimmedDescription,
                    shared: Boolean(shared),
                    accountId: preferenceAccountId,
                    security: preferenceId
                        ? (preferences?.find((pref) => pref.id === preferenceId)?.security ?? OWN_VIEW_SECURITY)
                        : OWN_VIEW_SECURITY,
                    columnVisibilityModel,
                    orderedFields,
                    ...(gridSettings ? { gridSettings } : {})
                };
            } catch {
                // useSavePreferences already toasts preference.saved.error.
                return null;
            } finally {
                setSavingView(false);
            }
        },
        [
            enabled,
            userProfile?.accountId,
            savePreferences,
            preferenceKey,
            activeOrganizationAccount,
            organizationAccountDetails?.organizationName,
            organizationAccountDetails?.adminEmail,
            preferences,
            application.name,
            refreshPreferences
        ]
    );

    const deleteView = useCallback(
        async (viewId: string): Promise<boolean> => {
            if (!enabled || !viewId) {
                return false;
            }

            setDeletingView(true);
            try {
                await deletePreference(viewId);
                const updatedPreferences = (preferences || []).filter((pref) => pref.id !== viewId);
                await refreshPreferences(undefined, updatedPreferences, false);
                successToastMessage('grid.column.views.delete.success');
                return true;
            } catch (error) {
                failedToastMessage('grid.column.views.delete.failed');
                return false;
            } finally {
                setDeletingView(false);
            }
        },
        [enabled, deletePreference, preferences, refreshPreferences]
    );

    return {
        savedViews,
        saveView,
        deleteView,
        savingView,
        deletingView,
        preferenceKey
    };
}
